// Package Block
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Field, ErrorMessage } from "formik";
import { connect, useDispatch } from "react-redux";
import { notification, Tooltip } from "antd";
import MetaTags from "react-meta-tags";
import { FormattedMessage, injectIntl } from "react-intl";
import { Input as AntInput } from "antd";

// Custom Component & Redux Block
import { actions } from "../_redux/authRedux";
import { login } from "../_redux/authCrud";
import { CustomInput } from "../../../../utility/CustomInput";
import { getDateTimeInZone, handleApiErrorResponse, TextError } from "../../Common/utils";
import * as Yup from "yup";
import { getAllChannels } from "../../Inventory/_redux/sale-stock-channels/saleStockChannelsCrud";
import { setDefaultChannel } from "../../Inventory/_redux/sale-stock-channels/saleStockChannelsActions";
import { getBasicSettingsForPortal } from "../../Estore/_redux/Estore/basicSettingsCrud";
import { isEmpty } from "lodash";

function Login(props) {
  const dispatch = useDispatch();
  const [isLoggedIn, setLoggedIn] = useState(false);
  const [token, setToken] = useState();
  const { intl } = props;
  const [loading, setLoading] = useState(false);
  const enableLoading = () => {
    setLoading(true);
  };

  const disableLoading = () => {
    setLoading(false);
  };

  const passwordCriteria = "Password must contain 8 characters. uppercase, lowercase, number and symbol.";

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("Username is required"),
    password: Yup.string()
      .required("Password is required")
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$#!%*?&])[A-Za-z\d@$#!%*?&]{8,}$/, passwordCriteria),
  });

  const redirectToPilot = (pilotUrl, accessToken, refreshToken) => {
    const redirectUrl = new URL(`${pilotUrl}pilot`);
    redirectUrl.searchParams.set("access_token", accessToken);
    redirectUrl.searchParams.set("refresh_token", refreshToken);
    window.location.href = redirectUrl.toString();
  };

  const openCustomerDisplay = (accessToken) => {
    // Get the current origin to build the customer display URL
    const baseUrl = window.location.origin;
    const customerDisplayUrl = `${baseUrl}/v2/customer-display`;

    // Open customer display in a new tab on the extended display
    // Using window.open with specific features to target extended display
    const newWindow = window.open(customerDisplayUrl, "customer-display", "width=1920,height=1080,left=1920,top=0,scrollbars=yes,resizable=yes");

    // If the window was blocked, try opening in a new tab
    if (!newWindow || newWindow.closed || typeof newWindow.closed === "undefined") {
      window.open(customerDisplayUrl, "_blank");
    }
  };

  const setDefaultChannelInStore = async (params) => {
    try {
      const response = await getAllChannels(params);
      const { entities } = response.data;
      const defaultChannel = {
        id: entities[0].id,
        name: entities[0].name,
      };

      dispatch(setDefaultChannel(defaultChannel));
      if (!localStorage.getItem("stock_search_filter")) {
        const stock_filter = {
          advance_filters: {
            channel_ids: [entities[0].id],
            brand_ids: [],
            supplier_ids: [],
            category_id: [],
            searchKey: "",
            searchText: "",
            search_aisle: "",
            search_zone: "",
            search_Bin: "",
            quantity_low_high: [],
            damaged_product: [],
            pageNumber: 1,
            pageSize: 10,
            product_status: 1,
          },
          display_advance_filters: false,
          channelForBulkEdit: [
            {
              id: entities[0].id,
              name: entities[0].name,
            },
          ],
          show_advance_filters: true,
        };
        localStorage.setItem("stock_search_filter", JSON.stringify({ ...stock_filter }));
      }

      if (!localStorage.getItem("stock_alert_filter")) {
        const stockAlertFilter = {
          advance_filters: {
            channel_id: [entities[0].id],
            brand_id: [],
            supplier_id: [],
            category_id: [],
            searchKey: "",
            min_qty: "",
            back_order_qty: "",
            pageNumber: 1,
            pageSize: 10,
          },
          display_advance_filters: false,
          show_advance_filters: true,
        };
        localStorage.setItem("stock_alert_filter", JSON.stringify({ ...stockAlertFilter }));
      }
    } catch (err) {
      handleApiErrorResponse(err);
    }
  };

  return (
    <>
      <MetaTags>
        <title>Kommerce Hub | Login</title>
        <meta
          name="description"
          content="Kommerce Hub - Empowering Commerce , On a mission to streamline your business operations and help you save your time and costs"
        />
      </MetaTags>
      <div className="login-form login-signin">
        <div className="text-center mb-10 mb-lg-20">
          <h3 className="font-size-h1">
            <FormattedMessage id="AUTH.LOGIN.TITLE" />
          </h3>
          <p className="text-muted font-weight-bold">Enter your username and password</p>
        </div>
        <Formik
          initialValues={{
            username: "",
            password: "",
          }}
          validationSchema={validationSchema}
          onSubmit={(values, { setStatus, setSubmitting }) => {
            enableLoading();
            return login(values.username, values.password)
              .then(({ data: { entity }, headers }) => {
                const { access_token, refresh_token } = entity || {};
                const pilotRedirectUrl = headers["x-pilot-redirect-url"];
                if (pilotRedirectUrl) {
                  redirectToPilot(pilotRedirectUrl, access_token, refresh_token);
                } else {
                  if (access_token && refresh_token) {
                    localStorage.setItem("access_token", access_token);
                  }
                  disableLoading();
                  setLoggedIn(true);
                  // localStorage.setItem("lastClearDate", new Date().toISOString().split("T")[0]);
                  // Dispatch login first so the Authorization header (read from Redux, not
                  // localStorage, by the axios interceptor) is available before any
                  // post-login API calls fire below.
                  try {
                    dispatch(actions.login(access_token, refresh_token, pilotRedirectUrl));
                    // Open customer display in new tab on extended display after successful login
                    // openCustomerDisplay(access_token);
                  } catch (error) {
                    console.error(error);
                  }
                  getBasicSettingsForPortal()
                    .then((res) => {
                      if (!isEmpty(res?.data?.entity?.timezone || {})) {
                        localStorage.setItem("lastClearDate", getDateTimeInZone(res.data.entity.timezone).split("T")[0]);
                      }
                    })
                    .catch((err) => {
                      handleApiErrorResponse(err);
                    });
                }
                setDefaultChannelInStore({ is_primary: true });
              })
              .catch((error) => {
                disableLoading();
                setSubmitting(false);

                if (error.response?.status === 403) {
                  handleApiErrorResponse(error);
                } else if (error.response?.status === 401) {
                  setStatus(
                    intl.formatMessage({
                      id: "AUTH.VALIDATION.INVALID_LOGIN",
                    })
                  );
                } else {
                  console.error(error);
                  setStatus(
                    "Please try again later. If the problem persists, contact support."
                  );
                }
              });
          }}
        >
          {({ values, status, setFieldValue, handleSubmit, isSubmitting }) => (
            <form className="form" noValidate={true} autoComplete="off" onSubmit={handleSubmit}>
              {status ? (
                <div role="alert" className="mb-10 alert alert-custom alert-light-danger alert-dismissible">
                  <div className="alert-text font-weight-bold">{status}</div>
                </div>
              ) : (
                ""
              )}

              <div className="form-group">
                <Field name="username" component={CustomInput} placeholder="Email or User Name" className="login-input-fields"></Field>
              </div>

              <div className="form-group">
                <Tooltip
                  title={<p className="password-tooltip">{passwordCriteria}</p>}
                  color={"#F0F0F0"}
                  overlayStyle={{ maxWidth: "350px" }}
                  placement="topLeft"
                >
                  <Field
                    autoFill="off"
                    type="password"
                    name="password"
                    className="login-input-fields"
                    value={values.password}
                    as={AntInput.Password}
                    placeholder="Password"
                    onChange={(e) => {
                      setFieldValue("password", e.target.value);
                    }}
                  />
                </Tooltip>
                <div>
                  <ErrorMessage name={"password"} component={TextError} />
                </div>
              </div>
              <div className="form-group d-flex flex-wrap justify-content-between align-items-center">
                <Link to="/auth/forgot-password" className="text-sm font-weight-bold" id="kt_login_forgot">
                  Forgot Password?
                </Link>

                <button id="kt_login_signin_submit" type="submit" disabled={isSubmitting} className={`btn btn-primary font-weight-bold px-9 py-4 my-3`}>
                  <span>Sign In</span>
                  {loading && <span className="ml-3 spinner spinner-white"></span>}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </>
  );
}

export default injectIntl(connect(null, actions)(Login));
